/**
 * Dispatch — the day, or the week.
 *
 * On a desk the day is a run sheet: technicians down the side, hours across,
 * and a job is booked by dragging it from Unassigned onto a tech and a time
 * (DISPATCH-2). The week is the same technicians against seven days; a job
 * dropped on a day goes in that tech's first gap, or keeps its time if it is
 * being moved from another day (DISPATCH-3). Dragging a job back onto
 * Unassigned takes it off the board.
 *
 * Dragging is not the only way: every unassigned job has a Book button with a
 * tech, a date and a time, because a phone cannot drag onto a grid it cannot
 * show, and a keyboard cannot drag at all.
 *
 * On a phone the day collapses to a list grouped by tech, and the week to a
 * list grouped by day, because a timeline grid is unreadable at 390px.
 */
import { type DragEvent, type MutableRefObject, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import {
  DAY_END,
  DAY_START,
  SLOTS,
  addDays,
  dayLabel,
  hhmm,
  localDate,
  localMinutes,
  minuteOfSlot,
  mondayOf,
  slotAt,
  slotsFor,
  todayIn,
} from "../lib/board";
import { money, slaLabel, time, titleCase, untilMinutes } from "../lib/format";
import { Card, Chip, ErrorState, Loading, PageHeader, Tile, Tiles, type Tone } from "../components/ui";
import { ToBookCard } from "../components/ToBookCard";
import { ToInvoiceCard } from "../components/ToInvoiceCard";
import type { DispatchAssign, DispatchAssignOut, DispatchDay, JobOut } from "../lib/types";

const TZ = "Australia/Adelaide";

const TYPE_TONE: Record<string, Tone> = {
  install: "accent",
  service: "info",
  maintenance: "ok",
  callout: "crit",
  quote_visit: "warn",
  warranty: "warn",
  project_stage: "accent",
};

/** Somebody is on site: the board shows it but will not move it. */
const PINNED = new Set(["on_site", "in_progress", "completed", "invoiced", "cancelled"]);

type View = "day" | "week";
type Said = { tone: "info" | "warn" | "crit"; text: string };

/** What is being dragged, kept outside `dataTransfer` because browsers hide
 *  its contents until the drop — and the ghost needs the job's length while
 *  it is still in the air. */
type Carrying = { job: JobOut } | null;

export default function Dispatch() {
  const [params, setParams] = useSearchParams();
  const view: View = params.get("view") === "week" ? "week" : "day";
  const picked = params.get("date") ?? todayIn(TZ);
  const first = view === "week" ? mondayOf(picked) : picked;
  const days = view === "week" ? 7 : 1;

  const go = (next: { view?: View; date?: string }) => {
    const p = new URLSearchParams(params);
    if (next.view) p.set("view", next.view);
    if (next.date) p.set("date", next.date);
    setParams(p, { replace: true });
  };

  const { can } = useAuth();
  const canDispatch = can(...OFFICE);
  const queryClient = useQueryClient();
  const [said, setSaid] = useState<Said | null>(null);
  const carrying = useRef<Carrying>(null);
  const [dragging, setDragging] = useState(false);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ["dispatch", first, days],
    queryFn: () => api.jobs.dispatch(first, days),
  });

  const assign = useMutation({
    mutationFn: (body: DispatchAssign) => api.jobs.assign(body),
    onSuccess: (out: DispatchAssignOut) => {
      void queryClient.invalidateQueries({ queryKey: ["dispatch"] });
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
      setSaid(describe(out, data));
    },
    onError: (err) => {
      void queryClient.invalidateQueries({ queryKey: ["dispatch"] });
      setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't move that job." });
    },
  });

  const drop = (body: Omit<DispatchAssign, "job_id" | "expected_rev">) => {
    const held = carrying.current;
    carrying.current = null;
    setDragging(false);
    if (!held) return;
    assign.mutate({ job_id: held.job.id, expected_rev: held.job.rev, ...body });
  };

  const drag = {
    enabled: canDispatch,
    carrying,
    start: (job: JobOut) => (e: DragEvent) => {
      carrying.current = { job };
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", job.number);
      // Nothing on the page may move while a job is in the air — clearing
      // the last notice here shifted the whole board up under the pointer.
      setDragging(true);
    },
    end: () => {
      carrying.current = null;
      setDragging(false);
    },
    drop,
  };

  const header = (
    <PageHeader
      title="Dispatch"
      actions={
        <div className="board-controls">
          {canDispatch && (
            <Link className="btn btn-primary btn-sm" to="/jobs/new">
              New job
            </Link>
          )}
          <div className="segmented" role="group" aria-label="View">
            <button type="button" className={view === "day" ? "active" : ""} onClick={() => go({ view: "day", date: first })}>
              Day
            </button>
            <button type="button" className={view === "week" ? "active" : ""} onClick={() => go({ view: "week", date: first })}>
              Week
            </button>
          </div>
          <div className="board-step">
            <button type="button" className="btn btn-sm" aria-label={`Previous ${view}`} onClick={() => go({ date: addDays(first, -days) })}>
              ‹
            </button>
            <button type="button" className="btn btn-sm" onClick={() => go({ date: todayIn(TZ) })}>
              Today
            </button>
            <button type="button" className="btn btn-sm" aria-label={`Next ${view}`} onClick={() => go({ date: addDays(first, days) })}>
              ›
            </button>
          </div>
          <label className="field field-inline">
            <span className="phone-hide">{view === "week" ? "Week of" : "Day"}</span>
            <input
              type="date"
              value={view === "week" ? first : picked}
              onChange={(e) => e.target.value && go({ date: e.target.value })}
            />
          </label>
        </div>
      }
    >
      {view === "week" ? `Week of ${dayLabel(first)}` : dayLabel(first)} — who is where, and what is about to go wrong.
    </PageHeader>
  );

  if (isPending)
    return (
      <div className="page">
        {header}
        <Loading what={view === "week" ? "Loading the week" : "Loading the day"} />
      </div>
    );
  if (error)
    return (
      <div className="page">
        {header}
        <ErrorState
          error={error}
          onRetry={() => void refetch()}
          needs="The dispatch board shows the whole company's day, which no single device carries."
        />
      </div>
    );

  const tz = data.timezone || TZ;

  return (
    <div className="page">
      {header}

      {/* Finished jobs nobody has billed: the office sees this first, every day (F5). */}
      <ToInvoiceCard />

      {/* Jobs a tech left unfinished, waiting for a return visit to be booked (D87). */}
      <ToBookCard />

      <Summary data={data} view={view} />

      {said && (
        <p className={`notice notice-${said.tone}`} role={said.tone === "crit" ? "alert" : "status"}>
          {said.text}
        </p>
      )}

      <Unassigned data={data} drag={drag} dragging={dragging} canDispatch={canDispatch} first={first} onBook={(b) => assign.mutate(b)} />

      {data.technicians.length === 0 ? (
        <Card title="Run sheet">
          <p className="state">Nobody is set up as a field technician yet. Add people under Settings → People.</p>
        </Card>
      ) : view === "day" ? (
        <Card
          title="Run sheet"
          subtitle={
            <>
              {DAY_START}:00 – {DAY_END}:00
              <span className="phone-hide"> · {canDispatch ? "drag a job onto a tech and a time" : "read only"}</span>
            </>
          }
          padded={false}
        >
          <RunSheet data={data} tz={tz} drag={drag} />
          <DayList data={data} />
        </Card>
      ) : (
        <Card
          title="The week"
          subtitle={
            <span className="phone-hide">
              {canDispatch ? "drag a job onto a tech and a day — it takes their first gap" : "read only"}
            </span>
          }
          padded={false}
        >
          <WeekGrid data={data} tz={tz} drag={drag} />
          <WeekList data={data} tz={tz} />
        </Card>
      )}
    </div>
  );
}

type Drag = {
  enabled: boolean;
  carrying: MutableRefObject<Carrying>;
  start: (job: JobOut) => (e: DragEvent) => void;
  end: () => void;
  drop: (body: Omit<DispatchAssign, "job_id" | "expected_rev">) => void;
};

/** What to say after a move: where it went, and anything it now overlaps. */
function describe(out: DispatchAssignOut, data: DispatchDay | undefined): Said {
  const job = out.job;
  if (!job.assigned_user_id) return { tone: "info", text: `${job.number} is back in Unassigned.` };
  const who = data?.technicians.find((t) => t.user_id === job.assigned_user_id)?.name ?? "them";
  const when = job.scheduled_start
    ? `${dayLabel(localDate(job.scheduled_start, data?.timezone || TZ))} at ${time(job.scheduled_start)}`
    : "";
  const base = `${job.number} booked for ${who}${when ? `, ${when}` : ""}.`;
  if (out.clashes.length === 0) return { tone: "info", text: base };
  const list = out.clashes.map((c) => `${c.number} (${time(c.scheduled_start)})`).join(", ");
  return { tone: "warn", text: `${base} It overlaps ${list} — move one if they can't do both.` };
}

function Summary({ data, view }: { data: DispatchDay; view: View }) {
  const stats = useMemo(() => {
    const all = [...data.jobs, ...data.unassigned];
    const breaching = all.filter((j) => {
      const mins = untilMinutes(j.sla_due_at);
      return mins !== null && mins < 120 && j.status !== "completed" && j.status !== "invoiced";
    });
    const value = data.jobs.reduce((sum, j) => sum + (j.quoted_cents || j.revenue_cents), 0);
    const booked = data.technicians.reduce((s, t) => s + t.booked_minutes, 0);
    const capacity = data.technicians.reduce((s, t) => s + t.capacity_minutes, 0);
    return { breaching, value, booked, capacity };
  }, [data]);

  return (
    <Tiles>
      <Tile
        label={view === "week" ? "Booked this week" : "Booked today"}
        value={data.jobs.length}
        detail={`${data.unassigned.length} unassigned`}
      />
      <Tile
        label="SLA at risk"
        value={stats.breaching.length}
        tone={stats.breaching.length > 0 ? "crit" : undefined}
        detail={stats.breaching[0] ? `Soonest ${slaLabel(stats.breaching[0].sla_due_at)}` : "Nothing inside two hours"}
      />
      <Tile label="Value on the board" value={money(stats.value)} detail="Quoted or booked" />
      <Tile
        label="Capacity used"
        value={stats.capacity ? `${Math.round((stats.booked / stats.capacity) * 100)}%` : "—"}
        detail={`${data.technicians.length} in the field`}
      />
    </Tiles>
  );
}

/* --------------------------------------------------------------- unassigned */

function Unassigned({
  data,
  drag,
  dragging,
  canDispatch,
  first,
  onBook,
}: {
  data: DispatchDay;
  drag: Drag;
  dragging: boolean;
  canDispatch: boolean;
  first: string;
  onBook: (body: DispatchAssign) => void;
}) {
  const [over, setOver] = useState(false);
  const [booking, setBooking] = useState<string | null>(null);
  const fromBoard = dragging && drag.carrying.current?.job.assigned_user_id;

  return (
    <div
      className={`drop-zone${over && fromBoard ? " drop-over" : ""}`}
      onDragOver={(e) => {
        if (!drag.enabled || !drag.carrying.current?.job.assigned_user_id) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        drag.drop({ user_id: null });
      }}
    >
      <Card
        title="Unassigned"
        subtitle={
          fromBoard
            ? "Drop here to take it off the board"
            : data.unassigned.length === 0
              ? "Nothing waiting"
              : `${data.unassigned.length} waiting${canDispatch ? " · drag onto the run sheet, or Book" : ""}`
        }
      >
        {data.unassigned.length === 0 && canDispatch && (
          <p className="tiny muted phone-hide">Drag a job from the board onto this card to take it off the board.</p>
        )}
        {data.unassigned.length > 0 && (
          <div className="stack">
            {data.unassigned.map((job) => (
              <div key={job.id}>
                <div className="unassigned-item">
                  <JobRow
                    job={job}
                    draggable={drag.enabled && !PINNED.has(job.status)}
                    onDragStart={drag.start(job)}
                    onDragEnd={drag.end}
                  />
                  {canDispatch && !PINNED.has(job.status) && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      aria-expanded={booking === job.id}
                      onClick={() => setBooking(booking === job.id ? null : job.id)}
                    >
                      Book
                    </button>
                  )}
                </div>
                {booking === job.id && (
                  <BookForm
                    job={job}
                    data={data}
                    first={first}
                    onCancel={() => setBooking(null)}
                    onBook={(b) => {
                      setBooking(null);
                      onBook(b);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/** The no-drag way to book: a tech, a day and a time. */
function BookForm({
  job,
  data,
  first,
  onBook,
  onCancel,
}: {
  job: JobOut;
  data: DispatchDay;
  first: string;
  onBook: (body: DispatchAssign) => void;
  onCancel: () => void;
}) {
  const [tech, setTech] = useState(data.technicians[0]?.user_id ?? "");
  const [day, setDay] = useState(first);
  const [at, setAt] = useState<string>("first");
  return (
    <div className="book-form">
      <label className="field">
        <span>Technician</span>
        <select value={tech} onChange={(e) => setTech(e.target.value)}>
          {data.technicians.map((t) => (
            <option key={t.user_id} value={t.user_id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Day</span>
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
      </label>
      <label className="field">
        <span>Time</span>
        <select value={at} onChange={(e) => setAt(e.target.value)}>
          <option value="first">First gap</option>
          {Array.from({ length: SLOTS }, (_, i) => minuteOfSlot(i)).map((m) => (
            <option key={m} value={m}>
              {hhmm(m)}
            </option>
          ))}
        </select>
      </label>
      <div className="btn-row">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!tech || !day}
          onClick={() =>
            onBook({
              job_id: job.id,
              user_id: tech,
              day,
              expected_rev: job.rev,
              ...(at === "first" ? {} : { start_minute: Number(at) }),
            })
          }
        >
          Book it
        </button>
        <button type="button" className="btn btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- the day */

/** Desktop timeline. Hidden on phones by CSS. */
function RunSheet({ data, tz, drag }: { data: DispatchDay; tz: string; drag: Drag }) {
  const [ghost, setGhost] = useState<{ tech: string; slot: number; span: number } | null>(null);

  return (
    <div className="runsheet">
      <div className="runsheet-inner">
        <div className="runsheet-hours">
          <div>Technician</div>
          {Array.from({ length: DAY_END - DAY_START }, (_, i) => (
            <div key={i} style={{ gridColumn: `span 2` }}>
              {DAY_START + i}:00
            </div>
          ))}
        </div>

        {data.technicians.map((tech) => {
          const jobs = data.jobs
            .filter((j) => j.assigned_user_id === tech.user_id && j.scheduled_start)
            .sort((a, b) => (a.scheduled_start ?? "").localeCompare(b.scheduled_start ?? ""));
          const load = tech.capacity_minutes ? Math.round((tech.booked_minutes / tech.capacity_minutes) * 100) : 0;
          const here = ghost?.tech === tech.user_id ? ghost : null;

          const pointAt = (e: DragEvent<HTMLDivElement>) => {
            const grid = e.currentTarget.querySelector(".runsheet-slots");
            if (!grid) return 0;
            const r = grid.getBoundingClientRect();
            return slotAt(e.clientX, r.left, r.width);
          };

          return (
            <div
              key={tech.user_id}
              className={`runsheet-row${here ? " drop-over" : ""}`}
              onDragOver={(e) => {
                const held = drag.carrying.current;
                if (!drag.enabled || !held) return;
                e.preventDefault();
                const slot = pointAt(e);
                const span = slotsFor(held.job);
                if (!here || here.slot !== slot) setGhost({ tech: tech.user_id, slot, span });
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setGhost(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                const slot = pointAt(e);
                setGhost(null);
                drag.drop({ user_id: tech.user_id, day: data.day, start_minute: minuteOfSlot(slot) });
              }}
            >
              <div className="runsheet-who">
                <b>{tech.name}</b>
                <small>{tech.role}</small>
                <div className="bar" style={{ marginTop: 5 }}>
                  <i
                    className={`bar-fill ${load > 100 ? "bar-warn" : "bar-accent"}`}
                    style={{ width: `${Math.min(load, 100)}%` }}
                  />
                </div>
              </div>
              <div className="runsheet-slots">
                {Array.from({ length: SLOTS }, (_, i) => (
                  <span key={i} />
                ))}
              </div>
              <div className="runsheet-lane">
                {here && (
                  <div
                    className="blk blk-ghost"
                    style={{ gridColumn: `${here.slot + 1} / span ${Math.min(here.span, SLOTS - here.slot)}` }}
                    aria-hidden
                  >
                    <span className="blk-title">{hhmm(minuteOfSlot(here.slot))}</span>
                  </div>
                )}
                {jobs.map((job) => {
                  const pos = slotFor(job, tz);
                  if (!pos) return null;
                  const movable = drag.enabled && !PINNED.has(job.status);
                  return (
                    <Link
                      key={job.id}
                      to={`/jobs/${job.id}`}
                      className={`blk blk-${TYPE_TONE[job.type] ?? "info"}${movable ? " blk-movable" : ""}`}
                      style={{ gridColumn: `${pos.start} / span ${pos.span}` }}
                      draggable={movable}
                      onDragStart={movable ? drag.start(job) : undefined}
                      onDragEnd={drag.end}
                      title={`${job.number} · ${time(job.scheduled_start)}–${time(job.scheduled_end)}${movable ? " · drag to move" : ""}`}
                    >
                      <span className="blk-title">{job.title}</span>
                      <span className="blk-meta">
                        {time(job.scheduled_start)} · {titleCase(job.type)}
                      </span>
                      <span className="blk-no mono">{job.number}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Map a scheduled window onto half-hour grid columns, clamped to the day. */
function slotFor(job: JobOut, tz: string): { start: number; span: number } | null {
  if (!job.scheduled_start) return null;
  const from = Math.max(DAY_START * 60, localMinutes(job.scheduled_start, tz));
  const endIso = job.scheduled_end ?? new Date(new Date(job.scheduled_start).getTime() + 3600_000).toISOString();
  let endMin = localMinutes(endIso, tz);
  if (endMin <= from) endMin = 24 * 60; // runs past midnight
  const to = Math.min(DAY_END * 60, Math.max(endMin, from + 30));
  if (to <= DAY_START * 60 || from >= DAY_END * 60) return null;
  return {
    start: Math.round((from - DAY_START * 60) / 30) + 1,
    span: Math.max(1, Math.round((to - from) / 30)),
  };
}

/** The phone version of the day. */
function DayList({ data }: { data: DispatchDay }) {
  return (
    <div className="run-list">
      {data.technicians.map((tech) => {
        const jobs = data.jobs.filter((j) => j.assigned_user_id === tech.user_id);
        return (
          <div key={tech.user_id} className="run-list-tech">
            <header>
              <b>{tech.name}</b>
              <span className="muted">
                {jobs.length === 0 ? "Nothing booked" : `${jobs.length} job${jobs.length > 1 ? "s" : ""}`}
              </span>
            </header>
            {jobs.map((job) => (
              <JobRow key={job.id} job={job} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------- the week */

function WeekGrid({ data, tz, drag }: { data: DispatchDay; tz: string; drag: Drag }) {
  const dates = Array.from({ length: data.days }, (_, i) => addDays(data.day, i));
  const today = todayIn(tz);
  const [over, setOver] = useState<string | null>(null);

  return (
    <div className="runsheet">
      <div className="week-inner">
        <div className="week-row week-head">
          <div>Technician</div>
          {dates.map((d) => (
            <div key={d} className={d === today ? "week-today" : undefined}>
              {dayLabel(d)}
            </div>
          ))}
        </div>

        {data.technicians.map((tech) => (
          <div key={tech.user_id} className="week-row">
            <div className="runsheet-who">
              <b>{tech.name}</b>
              <small>{tech.role}</small>
            </div>
            {dates.map((d, i) => {
              const key = `${tech.user_id}|${d}`;
              const jobs = data.jobs
                .filter((j) => j.assigned_user_id === tech.user_id && j.scheduled_start && localDate(j.scheduled_start, tz) === d)
                .sort((a, b) => (a.scheduled_start ?? "").localeCompare(b.scheduled_start ?? ""));
              const minutes = tech.booked_by_day[i] ?? 0;
              const load = Math.round((minutes / (8 * 60)) * 100);
              return (
                <div
                  key={d}
                  className={`week-cell${d === today ? " week-today" : ""}${over === key ? " drop-over" : ""}`}
                  onDragOver={(e) => {
                    if (!drag.enabled || !drag.carrying.current) return;
                    e.preventDefault();
                    if (over !== key) setOver(key);
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setOver(null);
                    drag.drop({ user_id: tech.user_id, day: d });
                  }}
                >
                  <div className="bar" title={`${Math.round(minutes / 6) / 10} h booked`}>
                    <i
                      className={`bar-fill ${load > 100 ? "bar-warn" : "bar-accent"}`}
                      style={{ width: `${Math.min(load, 100)}%` }}
                    />
                  </div>
                  {jobs.map((job) => {
                    const movable = drag.enabled && !PINNED.has(job.status);
                    return (
                      <Link
                        key={job.id}
                        to={`/jobs/${job.id}`}
                        className={`wk-job blk-${TYPE_TONE[job.type] ?? "info"}${movable ? " blk-movable" : ""}`}
                        draggable={movable}
                        onDragStart={movable ? drag.start(job) : undefined}
                        onDragEnd={drag.end}
                        title={`${job.number} · ${job.title}`}
                      >
                        <span className="mono">{time(job.scheduled_start)}</span> {job.title}
                      </Link>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/** The phone version of the week: by day, then by time. */
function WeekList({ data, tz }: { data: DispatchDay; tz: string }) {
  const dates = Array.from({ length: data.days }, (_, i) => addDays(data.day, i));
  const name = (id: string | null) => data.technicians.find((t) => t.user_id === id)?.name ?? "";
  return (
    <div className="run-list">
      {dates.map((d) => {
        const jobs = data.jobs
          .filter((j) => j.scheduled_start && localDate(j.scheduled_start, tz) === d)
          .sort((a, b) => (a.scheduled_start ?? "").localeCompare(b.scheduled_start ?? ""));
        return (
          <div key={d} className="run-list-tech">
            <header>
              <b>{dayLabel(d)}</b>
              <span className="muted">{jobs.length === 0 ? "Nothing booked" : `${jobs.length} job${jobs.length > 1 ? "s" : ""}`}</span>
            </header>
            {jobs.map((job) => (
              <JobRow key={job.id} job={job} who={name(job.assigned_user_id)} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ shared */

function JobRow({
  job,
  who,
  draggable,
  onDragStart,
  onDragEnd,
}: {
  job: JobOut;
  who?: string;
  draggable?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: () => void;
}) {
  const slaMins = untilMinutes(job.sla_due_at);
  const slaTone: Tone = slaMins === null ? "neutral" : slaMins < 0 ? "crit" : slaMins < 120 ? "warn" : "ok";

  return (
    <Link
      to={`/jobs/${job.id}`}
      className={`joblet joblet-${TYPE_TONE[job.type] ?? "info"}${draggable ? " blk-movable" : ""}`}
      draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined}
      onDragEnd={onDragEnd}
    >
      <div className="joblet-main">
        <span className="joblet-title">{job.title}</span>
        <span className="joblet-meta">
          <span className="mono">{job.number}</span> · {titleCase(job.type)}
          {job.scheduled_start && ` · ${time(job.scheduled_start)}`}
          {who && ` · ${who}`}
        </span>
      </div>
      <div className="joblet-side">
        {job.sla_due_at && (
          <Chip tone={slaTone} dot>
            {slaLabel(job.sla_due_at)}
          </Chip>
        )}
        <Chip tone="neutral">{titleCase(job.status)}</Chip>
      </div>
    </Link>
  );
}
